import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  companyReviews: [], // GET /staff-monthly-review/company — staff + reviewed flag + review for the given month
  companyReviewsMonth: null,
  history: [], // GET /staff-monthly-review/:employeeId/history
  loading: false,
  historyLoading: false,
  submitting: false,
  submitError: null
};

export const fetchCompanyMonthlyReviews = createAsyncThunk(
  'staffMonthlyReview/fetchCompany',
  async (month, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-monthly-review/company', {
        params: month ? { month } : undefined
      });
      return { data: response.data, month: month || null };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const submitStaffMonthlyReview = createAsyncThunk(
  'staffMonthlyReview/submit',
  async ({ employeeId, ratingDetails, comment }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/staff-monthly-review/${employeeId}`, {
        ...ratingDetails,
        comment
      });
      return { employeeId, review: response.data };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchStaffMonthlyReviewHistory = createAsyncThunk(
  'staffMonthlyReview/fetchHistory',
  async (employeeId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/staff-monthly-review/${employeeId}/history`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const staffMonthlyReviewSlice = createSlice({
  name: 'staffMonthlyReview',
  initialState,
  reducers: {
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    clearHistory: (state) => {
      state.history = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyMonthlyReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyMonthlyReviews.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.companyReviews = payload.data || [];
        state.companyReviewsMonth = payload.month;
      })
      .addCase(fetchCompanyMonthlyReviews.rejected, (state) => {
        state.loading = false;
      })

      .addCase(submitStaffMonthlyReview.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitStaffMonthlyReview.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.companyReviews = state.companyReviews.map((staff) =>
          staff._id === payload.employeeId
            ? { ...staff, reviewed: true, review: payload.review }
            : staff
        );
      })
      .addCase(submitStaffMonthlyReview.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload?.message || 'Failed to submit review';
      })

      .addCase(fetchStaffMonthlyReviewHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchStaffMonthlyReviewHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;
        state.history = payload || [];
      })
      .addCase(fetchStaffMonthlyReviewHistory.rejected, (state) => {
        state.historyLoading = false;
      });
  }
});

export const { clearSubmitError, clearHistory } = staffMonthlyReviewSlice.actions;

export default staffMonthlyReviewSlice.reducer;
